module Shared.Validations.PasswordStrength

open System
open Shared.Zxcvbn

type CharCounts =
    {
        AlphaCount: int
        NumericCount: int
        SpecialCount: int
        WhitespaceCount: int
        UpperCount: int
        LowerCount: int
    }

module CharCounts =
    open System

    let defaults =
        {
            AlphaCount = 0
            NumericCount = 0
            SpecialCount = 0
            WhitespaceCount = 0
            UpperCount = 0
            LowerCount = 0
        }

    let private (|NUMBER|_|) x = if Char.IsNumber(x) then Some x else None
    let private (|LETTER|_|) x = if Char.IsLetter(x) then Some x else None
    let private (|UPPERCASE|LOWERCASE|) x = if Char.IsUpper(x) then UPPERCASE else LOWERCASE
    let private (|WHITESPACE|_|) x = if Char.IsWhiteSpace(x) then Some x else None

    let ofString str =
        if String.IsNullOrEmpty str then defaults
        else 
            str.ToCharArray()
            |> Array.fold
                (fun acc c ->
                    match c with 
                    | NUMBER _ -> {acc with NumericCount = acc.NumericCount + 1}
                    | LETTER UPPERCASE ->
                        {acc with
                            AlphaCount = acc.AlphaCount + 1
                            UpperCount = acc.UpperCount + 1
                        }
                    | LETTER LOWERCASE ->
                        {acc with
                            AlphaCount = acc.AlphaCount + 1
                            LowerCount = acc.LowerCount + 1
                        }
                    | WHITESPACE _ -> {acc with WhitespaceCount = acc.WhitespaceCount + 1}
                    | _ -> {acc with SpecialCount = acc.SpecialCount + 1}
                )
                defaults

module Validations =
    open Illy.SafeStack.Validation
    open Illy.SafeStack.CompositionalValidation
    open Illy.SafeStack.ROP

    type PasswordStrengthValidations =
        | MustHaveMinLength of minLength: int
        | MustHaveMaxLength of maxLength: int
        | MustHaveMinAlphaChars of minChars: int
        | MustHaveMaxAlphaChars of maxChars: int
        | MustHaveMinNumericChars of minChars: int
        | MustHaveMaxNumericChars of maxChars: int
        | MustHaveMinWhitespaceChars of minChars: int
        | MustHaveMaxWhitespaceChars of maxChars: int
        | MustHaveMinSpecialChars of minChars: int
        | MustHaveMaxSpecialChars of maxChars: int
        | MustHaveMinUpperChars of minChars: int
        | MustHaveMaxUpperChars of maxChars: int
        | MustHaveMinLowerChars of minChars: int
        | MustHaveMaxLowerChars of maxChars: int
        | MustHaveMinScore

    module PasswordStrengthValidations =
        let toString =
            let minError minCount desc = sprintf "Password requires at least %d %s character%s." minCount desc (if minCount <> 1 then "s" else "")
            let maxError maxCount desc = sprintf "Password must have no more than %d %s character%s." maxCount desc (if maxCount <> 1 then "s" else "")

            function
            | MustHaveMinLength min -> sprintf "Password must be at least %d character%s." min (if min <> 1 then "s" else "")
            | MustHaveMaxLength max -> sprintf "Password must be no more than %d character%s." max (if max <> 1 then "s" else "")
            | MustHaveMinAlphaChars min -> minError min "alphabet"
            | MustHaveMaxAlphaChars max -> maxError max "alphabet"
            | MustHaveMinNumericChars min -> minError min "numeric"
            | MustHaveMaxNumericChars max -> maxError max "numeric"
            | MustHaveMinWhitespaceChars min -> minError min "whitespace"
            | MustHaveMaxWhitespaceChars max -> maxError max "whitespace"
            | MustHaveMinSpecialChars min -> minError min "special"
            | MustHaveMaxSpecialChars max -> maxError max "special"
            | MustHaveMinUpperChars min -> minError min "uppercase"
            | MustHaveMaxUpperChars max -> maxError max "uppercase"
            | MustHaveMinLowerChars min -> minError min "lowercase"
            | MustHaveMaxLowerChars max -> maxError max "lowercase"
            | MustHaveMinScore -> "Password is not strong enough."

    let validatePasswordStrength
        (config: PasswordConfig)
        (scorePassword: ZxcvbnList -> string -> PasswordScore)
        str =
        let min minCount actual raiseError =
            !!(error (raiseError minCount))
            >>= conditional pass (fun _ -> minCount <= actual)
            |> unwrap
        let max maxCount actual raiseError =
            maxCount
            |> Option.map
                (fun maxCount ->
                    !!(error (raiseError maxCount))
                    >>= conditional pass (fun _ -> maxCount >= actual || maxCount = -1)
                    |> unwrap
                )
            |> Option.defaultValue pass

        validate {
            let str = str |> Option.defaultValue ""
            if str |> String.IsNullOrEmpty |> not then
                let counts = CharCounts.ofString str
                return! min config.PasswordStrength.MinLength (String.length str) MustHaveMinLength
                return! max config.PasswordStrength.MaxLength (String.length str) MustHaveMaxLength
                return! min config.PasswordStrength.MinAlpha counts.AlphaCount MustHaveMinAlphaChars
                return! max config.PasswordStrength.MaxAlpha counts.AlphaCount MustHaveMaxAlphaChars
                return! min config.PasswordStrength.MinNumeric counts.NumericCount MustHaveMinNumericChars
                return! max config.PasswordStrength.MaxNumeric counts.NumericCount MustHaveMaxNumericChars
                return! min config.PasswordStrength.MinWhitespace counts.WhitespaceCount MustHaveMinWhitespaceChars
                return! max config.PasswordStrength.MaxWhitespace counts.WhitespaceCount MustHaveMaxWhitespaceChars
                return! min config.PasswordStrength.MinSpecial counts.SpecialCount MustHaveMinSpecialChars
                return! max config.PasswordStrength.MaxSpecial counts.SpecialCount MustHaveMaxSpecialChars
                return! min config.PasswordStrength.MinUpper counts.UpperCount MustHaveMinUpperChars
                return! max config.PasswordStrength.MaxUpper counts.UpperCount MustHaveMaxUpperChars
                return! min config.PasswordStrength.MinUpper counts.LowerCount MustHaveMinLowerChars
                return! max config.PasswordStrength.MaxUpper counts.LowerCount MustHaveMaxLowerChars

                return! !!(error MustHaveMinScore)
                    >>= conditional pass
                        (fun _ ->
                            scorePassword config.ZxcvbnList str >= config.MinScore
                        )
                    |> unwrap
        }